import {
  Banknote,
  CreditCard,
  Gift,
  Layers,
  type LucideIcon,
} from 'lucide-react';

import { CustomClassBooking } from '@/lib/types';
import { formatPrice } from '@/lib/utils';

type PaymentSummary = NonNullable<CustomClassBooking['payment_summary']>;

type PaymentDisplay = {
  icon: LucideIcon;
  title: string;
  detail: string;
};

const joinDetail = (status: string, subline: string) =>
  [status, subline].filter(Boolean).join(' · ');

const SETTLED_WORD: Partial<Record<PaymentSummary['status'], string>> = {
  PAID: 'Paid',
  PAID_OFFLINE: 'Paid',
  REFUNDED: 'Refunded',
};

// Viewer-scoped: the viewer's own money only. covers_count is a head count,
// never names.
export const getCustomBookingPaymentDisplay = (
  payment: PaymentSummary
): PaymentDisplay => {
  const subline =
    payment.covers_count > 1 ? `Covers ${payment.covers_count} people` : '';
  const amount = formatPrice(payment.amount, payment.currency);

  // The backend prices FREE and CREDITS rows at amount 0, so the method must
  // be decided before any zero-amount shortcut.
  switch (payment.method) {
    case 'FREE':
      return { icon: Gift, title: 'Free', detail: subline };
    case 'CREDITS':
      return {
        icon: Layers,
        title: 'Credits',
        detail: joinDetail(
          payment.status === 'REFUNDED'
            ? 'Refunded'
            : payment.status === 'PAID'
              ? ''
              : 'Pending',
          subline
        ),
      };
    case 'PAID_OFFLINE':
      if (payment.amount === 0)
        return { icon: Gift, title: 'Free', detail: subline };
      return {
        icon: Banknote,
        title: amount,
        detail: joinDetail(
          payment.status === 'REFUNDED' ? 'Refunded' : 'Paid',
          subline
        ),
      };
    case 'PAYMENT_LINK': {
      if (payment.amount === 0)
        return { icon: Gift, title: 'Free', detail: subline };

      const settled = SETTLED_WORD[payment.status];
      if (settled)
        return {
          icon: CreditCard,
          title: amount,
          detail: joinDetail(settled, subline),
        };

      // Money still owed leads the row, so a glance cannot read an unpaid
      // booking as a settled one. A status we don't know about lands here too.
      return {
        icon: CreditCard,
        title:
          payment.status === 'LINK_CANCELED'
            ? 'Payment cancelled'
            : 'Payment pending',
        detail: joinDetail(amount, subline),
      };
    }
    default: {
      payment.method satisfies never;
      return { icon: Banknote, title: amount, detail: subline };
    }
  }
};
